<template>
    <app-layout title="Edit Customer">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Edit Customer</template>

                <template #actions>
                    <!-- Business Central Sync button -->
                    <jet-button @click="customerSync" :disabled="form.processing" >
                        BC Sync
                    </jet-button>

                    <jet-button v-show="form.isDirty" type="submit" :disabled="form.processing" >
                        Save
                    </jet-button>

                   <jet-tertiary-button
                        v-if="form.isDirty"
                        :disabled="form.processing"
                        @click="showUnsavedChangesModal = true"
                    >
                        Cancel
                    </jet-tertiary-button>

                    <jet-tertiary-button
                        v-else
                        :disabled="form.processing"
                        :href="backUrl"
                    >
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <customer-navigation :customer="customer" />

            <div class="px-8 xl:flex">
                <customer-form
                    :form="form"
                    :sales-reps="salesReps"
                    :delivery-options="deliveryOptions"
                    :type-options="typeOptions"
                    :route-options="routeOptions"
                    :pricing-options="pricingOptions"
                    :payment-terms-options="paymentTermsOptions"
                    :payment-method-options="paymentMethodOptions"
                />

                <customer-pricing-form
                    v-if="hasFinanceRole"
                    :form="form"
                    :products="products"
                />
            </div>

            <div class="w-full text-center my-8">
                <jet-danger-button @click="showDeleteModal = true">Delete Customer</jet-danger-button>
            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(backUrl)"
        />

        <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal=false">
            <template #title>
                Delete Customer
            </template>

            <template #content>
                Are you sure you would like to delete this customer?
            </template>

            <template #footer>
                <jet-tertiary-button @click="showDeleteModal=false">
                    Cancel
                </jet-tertiary-button>

                <jet-danger-button class="ml-2" @click="deleteCustomer" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Delete
                </jet-danger-button>
            </template>
        </jet-confirmation-modal>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import { usePage } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import CustomerNavigation from '@/Shared/CustomerNavigation';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetNavLink from '@/Jetstream/NavLink';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import CustomerForm from '@/Pages/Customer/Partials/CustomerForm';
import CustomerPricingForm from '@/Pages/Customer/Partials/CustomerPricingForm';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const form = useForm({
            id: props.customer.id,
            enabled: props.customer.enabled,
            sales_rep_id: props.customer.sales_rep_id,
            pricing_level_id: props.customer.pricing_level_id,
            tax_exempt: props.customer.tax_exempt,
            show_picksheet_pricing: props.customer.show_picksheet_pricing,
            type: props.customer.type,
            name: props.customer.name,
            address_1: props.customer.address_1,
            address_2: props.customer.address_2,
            city: props.customer.city,
            postal_code: props.customer.postal_code,
            province: props.customer.province,
            phone: props.customer.phone,
            cell: props.customer.cell,
            fax: props.customer.fax,
            email: props.customer.email,
            ap_email: props.customer.ap_email,
            contact_name: props.customer.contact_name,
            default_shipping_method: props.customer.default_shipping_method,
            route: props.customer.route,
            delivery_days: props.customer.delivery_days.split(','),
            delivery_duration: props.customer.delivery_duration,
            delivery_order_minimum: props.customer.delivery_order_minimum,
            delivery_fee: props.customer.delivery_fee,
            delivery_window_start: props.customer.delivery_window_start,
            delivery_window_end: props.customer.delivery_window_end,
            auto_apply_credit: props.customer.auto_apply_credit,
            credit_balance: props.customer.credit_balance,
            internal_notes: props.customer.internal_notes,
            order_notes: props.customer.order_notes,
            load_notes: props.customer.load_notes,
            payment_terms: props.customer.payment_terms,
            payment_method: props.customer.payment_method,
            pricing: props.customer.pricing.map(pricing => {
                return {
                    product_id: pricing.product_id,
                    price: pricing.price.toFixed(2),
                    base_price: pricing.product.price,
                    cost: pricing.product.cost,
                    title: pricing.product.title,
                    size: pricing.product.size,
                    modifier_type: pricing.modifier_type,
                    modifier_amount: pricing.modifier_amount
                }
            })
        })

        const { hasFinanceRole } = UseUserAuth(usePage().props.value.user);

        return { form, hasFinanceRole }
    },

    components: {
        AppLayout,
        PageHeader,
        CustomerNavigation,
        JetButton,
        JetConfirmationModal,
        JetDangerButton,
        JetNavLink,
        JetTertiaryButton,
        CustomerForm,
        CustomerPricingForm,
        UnsavedChangesModal2,
    },

    props: [
        'customer', 
        'salesReps', 
        'pricingOptions', 
        'typeOptions', 
        'routeOptions', 
        'deliveryOptions', 
        'products',
        'paymentTermsOptions', 
        'paymentMethodOptions'
    ],

    data() {
        return {
            formRoute: this.route('customers.update', this.customer.id),
            showDeleteModal: false,
            showUnsavedChangesModal: false,
        }
    },

    computed: {
        backUrl () {
            return route('customers.index', {
                page: this.$page.props.page.customers,
            });
        }
    },

    methods: {
        async customerSync() {
            this.form.processing = true;
            try {
                const response = await axios.post('/business-central/customerSync', {
                    customer_id: this.form.id,
                    type: 'wholesale'
                });

                console.log(response.data.message);
            } catch (error) {
                console.error(error);
                
                if (error.response && error.response.data && error.response.data.message) {
                    console.error(error.response.data.message);
                } else {
                    console.error('An unknown error occurred.');
                }

                alert('An error occurred while syncing the product. Please check the eror logs.');
            } finally {
                this.form.processing = false;
            }
        },

        submit () {
            this.form.submit('put', route('customers.update', this.customer.id));
        },

        deleteCustomer () {
            this.$inertia.delete(route('customers.destroy', this.customer.id), null, {
                onError: (errors) => {
                    console.log(errors);
                }
            });
        },
    },
}
</script>

<style scoped>

</style>
